import { Query, useChainQueries } from "@Light/components/app/QueryLoader";
import { usePaymentSession } from "@Light/components/page/payment/PaymentSession";
import { unstyledInputClassName } from "@Light/components/system/Input";
import { useLight } from "@Light/services/light";
import type { PaymentMethodAddedRequest } from "@Light/services/lightTypes";
import { useMutation } from "@Light/utils/mutation";
import { captureException, captureMessage } from "@sentry/react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import clsx from "clsx";
import { useEffect, useState } from "react";
import {
  FieldValues,
  UseFormRegister,
  useForm,
  useWatch,
} from "react-hook-form";

export type UpdatePaymentMethodForm = {
  onSubmit: () => Promise<void>;
  mutation: Query;
  form: React.ReactNode;
  stripeError?: string;
  miscError?: string;
};

export type PersistPaymentMethod = {
  mutate: (paymentMethodId: string) => void;
  query: Query;
};

export function useUpdatePaymentMethodForm(
  collectZip: boolean,
  persist?: PersistPaymentMethod,
): UpdatePaymentMethodForm {
  const [stripeQuery, setStripeQuery] = useState<Query>({
    isUninitialized: true,
  });
  const [stripeError, setStripeError] = useState<string | undefined>(undefined);
  return useUpdatePaymentMethodFormB1({
    stripeQuery,
    setStripeQuery,
    stripeError,
    setStripeError,
    collectZip,
    persist,
  });
}

export type UseUpdatePaymentMethodFormB1Props = {
  stripeQuery: Query;
  setStripeQuery: (v: Query) => void;
  stripeError: string | undefined;
  setStripeError: (v: string | undefined) => void;
  collectZip: boolean;
  persist?: PersistPaymentMethod;
};

export function useUpdatePaymentMethodFormB1({
  stripeQuery,
  setStripeQuery,
  stripeError,
  setStripeError,
  collectZip,
  persist: persistOverride,
}: UseUpdatePaymentMethodFormB1Props): UpdatePaymentMethodForm {
  const { useEnsurePaymentMethodAddedMutation } = useLight();
  const ensurePaymentMethodAdded = useMutation<PaymentMethodAddedRequest>(
    useEnsurePaymentMethodAddedMutation,
  );
  const persist: PersistPaymentMethod = persistOverride ?? {
    mutate: (paymentMethodId) => {
      ensurePaymentMethodAdded.mutate({ payment_method_id: paymentMethodId });
    },
    query: ensurePaymentMethodAdded,
  };
  const { register, handleSubmit, setValue, control } = useForm();

  const zip = useWatch({ control, name: "zip" });

  useEffect(() => {
    if (!zip) {
      return;
    }

    // strip any non-digit characters
    let digits = zip.replace(/\D/g, "");

    if (digits.length > 5) {
      digits = digits.slice(0, 5);
    }

    if (digits !== zip) {
      setValue("zip", digits);
    }
  }, [zip, setValue]);

  const stripe = useStripe();
  const elements = useElements();
  const paymentSession = usePaymentSession();

  const client_secret = paymentSession.client_secret;
  const onSubmit = handleSubmit(({ zip }) => {
    if (!stripe || !elements || stripeQuery.isLoading || !client_secret) {
      captureMessage("Failed to update payment method", {
        extra: {
          isSubmitting: Boolean(stripeQuery.isLoading),
          noClientSecret: !client_secret,
          noElements: !elements,
          noStripe: !stripe,
        },
      });
      return;
    }

    setStripeQuery({
      isLoading: true,
    });
    setStripeError(undefined);

    const cardNumberEl = elements.getElement(CardNumberElement);
    if (!cardNumberEl) {
      setStripeQuery({ isError: true });
      captureMessage("Card number not found");
      return;
    }

    stripe
      .confirmCardSetup(client_secret, {
        // Format: https://docs.stripe.com/api/payment_methods/object
        payment_method: {
          card: cardNumberEl,
          billing_details: {
            address: {
              postal_code: zip ?? undefined,
            },
          },
        },
      })
      .then(({ error, setupIntent }) => {
        if (error?.message) {
          setStripeQuery({ isError: true });
          setStripeError(error.message);
          captureMessage("Stripe error", { extra: { error: error.message } });
          return;
        }
        setStripeError(undefined);

        if (!setupIntent?.payment_method) {
          setStripeQuery({ isError: true });
          captureMessage("No payment method returned from Stripe");
          return;
        }

        setStripeQuery({ isSuccess: true });
        persist.mutate(setupIntent.payment_method as string);
      })
      .catch((error: unknown) => {
        setStripeQuery({ isError: true });
        setStripeError(undefined);
        captureException(error);
      });
  });

  const mutation = useChainQueries([stripeQuery, persist.query]);
  if (!stripe || !elements) {
    return {
      onSubmit,
      mutation,
      form: null,
      stripeError: stripeError,
    };
  }

  return {
    onSubmit,
    mutation,
    form: <UpdatePaymentMethod register={register} collectZip={collectZip} />,
    stripeError: stripeError,
  };
}

// Match styles to components/system/Input.tsx as closely as possible. Stripe
// styles its fields through this object rather than CSS, so our tokens have to
// be resolved to literal values here.
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      // --font-body
      fontFamily: 'Inter, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
      fontSmoothing: "antialiased",
      // text-md
      fontSize: "16px",
      lineHeight: "24px",
      color: "#181d27", // --color-text-primary
      "::placeholder": {
        color: "#717680", // --color-text-placeholder
      },
      ":-webkit-autofill": {
        color: "#181d27", // --color-text-primary
      },
    },
    invalid: {
      color: "#d4122c", // --color-text-error-primary
      iconColor: "#d4122c", // --color-text-error-primary
    },
  },
};

type CardFieldFocusProps = {
  onFocus: () => void;
  onBlur: () => void;
};

const CardField = ({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: (focusProps: CardFieldFocusProps) => React.ReactNode;
}) => {
  // Focus must be tracked in JS due to input being in an iframe.
  const [isFocused, setIsFocused] = useState(false);
  return (
    <div className={clsx("flex flex-col gap-1.5", className)}>
      <label className="text-sm font-medium text-secondary">{label}</label>
      <div
        className={clsx(
          "flex min-h-11 items-center rounded-md border bg-primary px-3.25 py-2.25 shadow-xs",
          isFocused
            ? "border-brand ring-4 ring-focus-ring/24"
            : "border-secondary",
        )}
      >
        <div className="min-w-0 flex-1">
          {children({
            onFocus: () => {
              setIsFocused(true);
            },
            onBlur: () => {
              setIsFocused(false);
            },
          })}
        </div>
      </div>
    </div>
  );
};

export type UpdatePaymentMethodProps = {
  register: UseFormRegister<FieldValues>;
  collectZip: boolean;
};

export const UpdatePaymentMethod: React.FC<UpdatePaymentMethodProps> = ({
  register,
  collectZip,
}) => {
  return (
    <div className="flex flex-col gap-6">
      <CardField label="Card number">
        {(focusProps) => (
          <CardNumberElement
            options={{ ...CARD_ELEMENT_OPTIONS, showIcon: true }}
            {...focusProps}
          />
        )}
      </CardField>
      <div className="flex gap-6">
        <CardField label="Expiration date" className="flex-1">
          {(focusProps) => (
            <CardExpiryElement options={CARD_ELEMENT_OPTIONS} {...focusProps} />
          )}
        </CardField>
        <CardField label="Security code" className="flex-1">
          {(focusProps) => (
            <CardCvcElement options={CARD_ELEMENT_OPTIONS} {...focusProps} />
          )}
        </CardField>
        {collectZip && (
          <CardField label="ZIP" className="flex-1">
            {(focusProps) => (
              <input
                type="text"
                inputMode="numeric"
                autoComplete="postal-code"
                placeholder="78701"
                maxLength={5}
                className={unstyledInputClassName}
                {...register("zip", {
                  required: true,
                  minLength: 5,
                  maxLength: 5,
                })}
                {...focusProps}
              />
            )}
          </CardField>
        )}
      </div>
    </div>
  );
};
